// src/pages/CompliancePage.tsx

import { FC } from 'react';
import styles from '../css/CompliancePage.module.css';
import { motion, Variants } from 'framer-motion';
import { FileCheck, Shield, ArrowLeft, Landmark, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

// Shared Components
import AnimatedSection from '../components/Shared/AnimatedSection';

const CompliancePage: FC = () => {
    const lastUpdated = "October 24, 2025";

    const containerVariants: Variants = { 
        visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } } 
    };
    
    const itemVariants: Variants = { 
        hidden: { opacity: 0, y: 20 }, 
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } 
    };

    return (
        <div className={styles.compliancePage}>
            
            {/* 1. HERO SECTION */}
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    <div className={styles.heroContent}>
                        <motion.div 
                            className={styles.heroWrapper}
                            initial="hidden" animate="visible" variants={containerVariants}
                        >
                            <motion.span variants={itemVariants} className={styles.heroBadge}>
                                Regulatory Standards
                            </motion.span>
                            
                            <motion.h1 variants={itemVariants} className={styles.heroTitle}>
                                Global Compliance & <br/>
                                <span className={styles.titleHighlight}>Security Frameworks.</span>
                            </motion.h1>
                            
                            <motion.p variants={itemVariants} className={styles.heroSubtitle}>
                                We adhere to rigorous international standards to ensure the integrity, confidentiality, and availability of your financial data.
                            </motion.p>

                            <motion.div variants={itemVariants} className={styles.metaInfo}>
                                <span className={styles.dateLabel}>Status: Active • Audited Annually</span>
                            </motion.div>
                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>

            {/* 2. COMPLIANCE CONTENT */}
            <AnimatedSection className={styles.contentSection}>
                <div className={styles.container}>
                    <div className={styles.documentLayout}>
                        
                        {/* Sidebar Navigation */}
                        <aside className={styles.sidebar}>
                            <div className={styles.sidebarSticky}>
                                <div className={styles.iconBlock}>
                                    <FileCheck size={24} strokeWidth={1.5} />
                                </div>
                                <h3 className={styles.sidebarTitle}>Framework Index</h3>
                                <ul className={styles.tocList}>
                                    <li>1. InfoSec (ISO/SOC)</li>
                                    <li>2. Data Privacy (GDPR)</li>
                                    <li>3. Financial (IFRS)</li>
                                    <li>4. Accessibility</li>
                                    <li>5. Audit Reports</li>
                                </ul>
                                <Link to="/" className={styles.backLink}>
                                    <ArrowLeft size={14} /> Return Home
                                </Link>
                            </div>
                        </aside>

                        {/* Main Content */}
                        <div className={styles.legalTextWrapper}>
                            
                            <div className={styles.legalSection}>
                                <h2>1. Information Security</h2>
                                <p>
                                    OTA TECH Inc. operates under a "Security-First" doctrine. Our operational environment is designed to meet the following benchmarks:
                                </p>
                                <div className={styles.certGrid}>
                                    <div className={styles.certCard}>
                                        <Shield size={20} className={styles.certIcon} />
                                        <div>
                                            <h4>ISO 27001</h4>
                                            <p>Certified Information Security Management System (ISMS).</p>
                                        </div>
                                    </div>
                                    <div className={styles.certCard}>
                                        <Shield size={20} className={styles.certIcon} />
                                        <div>
                                            <h4>SOC 2 Type II</h4>
                                            <p>Audited controls for security, availability, and confidentiality.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>2. Data Privacy & Sovereignty</h2>
                                <p>
                                    We comply with major global data protection regulations to ensure your users' rights are respected across borders.
                                </p>
                                <ul>
                                    <li><strong>GDPR (Europe):</strong> Full compliance with the General Data Protection Regulation for all EU data subjects.</li>
                                    <li><strong>CCPA (California):</strong> Adherence to the California Consumer Privacy Act regarding data transparency.</li>
                                    <li><strong>DPA (Ghana):</strong> Compliance with the Data Protection Act, 2012 (Act 843) for local operations.</li>
                                </ul>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>3. Financial Integrity</h2>
                                <p>
                                    As a builder of ERP and FinTech solutions, our core logic adheres to standard accounting principles.
                                </p>
                                <div className={styles.highlightBox}>
                                    <Landmark size={20} className={styles.highlightIcon} />
                                    <p>
                                        <strong>IFRS & GAAP Ready:</strong> Our algorithms are validated by Chartered Accountants to ensure reporting output aligns with International Financial Reporting Standards (IFRS) and Generally Accepted Accounting Principles (GAAP).
                                    </p>
                                </div>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>4. Accessibility (WCAG)</h2>
                                <p>
                                    We believe enterprise tools should be accessible to everyone.
                                </p>
                                <p>
                                    Our user interfaces, including the "ANNA AI" dashboard, are designed to meet <strong>WCAG 2.1 AA</strong> standards, ensuring usability for individuals with visual, auditory, or motor impairments.
                                </p>
                            </div>

                            <div className={styles.legalSection}>
                                <h2>5. Request Audit Reports</h2>
                                <p>
                                    Enterprise clients and partners may request access to our detailed compliance packages and penetration test summaries.
                                </p>
                                <div className={styles.requestBox}>
                                    <div className={styles.requestHeader}>
                                        <CheckCircle size={18} /> <span>Compliance Portal</span>
                                    </div>
                                    <p>
                                        Access relies on a signed Non-Disclosure Agreement (NDA).
                                    </p>
                                    <Link to="/contact" className={styles.requestLink}>
                                        Request Access via Contact Form &rarr;
                                    </Link>
                                </div>
                            </div>

                        </div>
                    </div>
                </div>
            </AnimatedSection>

        </div>
    );
};

export default CompliancePage;